import {
  DayEnum,
  LabelPreviewWithoutTypeOutDTO,
  LovDTO,
  OpeningEntityTypeEnum,
  OpeningHourDefinitionsDTO,
  OpeningHourDefinitionsInDTO,
  OpeningModeEnum,
  TmplV2DetailOutDTO,
} from '@api/ringil-api.v2.tsx';
import { generateUUID } from '@utils/utils.tsx';
import { LocalityEntryModeEnum } from '@app/pages/expedition/create/types.ts';
import { fromContractedCarrierOutDTO, fromPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';
import { newPriceTable } from '@utils/contractedPriceTable.ts';
import { LineForm, LineStop } from '@app/pages/customer/LineEdit/types.ts';
import { DEFAULT_LINE_INTERVAL_HOURS } from '@app/pages/customer/LineEdit/lineToSaveRequest.ts';
import { pickAccessibleFolders } from '@app/pages/expedition/create/mappers/lineToForm.ts';

/**
 * The detail carries only ids for the internal-info pickers; the tags display names, so the caller
 * hands in the lists the names are looked up in.
 */
export interface LineFormLookups {
  persons: LovDTO[];
  folders: LovDTO[];
  labels: LabelPreviewWithoutTypeOutDTO[];
  partners: LovDTO[];
}

const ALL_DAYS = [DayEnum.MONDAY, DayEnum.TUESDAY, DayEnum.WEDNESDAY, DayEnum.THURSDAY, DayEnum.FRIDAY, DayEnum.SATURDAY, DayEnum.SUNDAY];

/** All seven days, closed unless the stored definition says otherwise (the editor expects the full week). */
export const normalizeOpeningHours = (stored: OpeningHourDefinitionsDTO | OpeningHourDefinitionsInDTO | null | undefined): OpeningHourDefinitionsInDTO => ({
  id: stored?.id ?? null,
  entityId: stored?.entityId ?? null,
  openingEntityType: OpeningEntityTypeEnum.TMPL_V2_STOP,
  openingHoursDays: ALL_DAYS.map(day => {
    const found = stored?.openingHoursDays?.find(d => d.day === day);
    return found
      ? { day, openingMode: found.openingMode, openingHours: (found.openingHours ?? []).map(h => ({ since: h.since, till: h.till })) }
      : { day, openingMode: OpeningModeEnum.CLOSED, openingHours: [] };
  }),
});

const lov = (list: LovDTO[], id: string | undefined): LovDTO | undefined => {
  if (!id) return undefined;
  return list.find(i => i.id === id) ?? { id, name: id, active: true };
};

/** What a line without a stored price table starts with. */
export interface LineFormDefaults {
  currency: string;
  levelPrefix: string;
}

export const detailToLineForm = (
  detail: TmplV2DetailOutDTO,
  lookups: LineFormLookups,
  { currency, levelPrefix }: LineFormDefaults = { currency: 'EUR', levelPrefix: 'Úroveň' },
): LineForm => {
  // carriers' cells are keyed by the table's rows, so the table is mapped first
  const priceTable = detail.distribution?.priceTable ? fromPriceTableDTO(detail.distribution.priceTable) : newPriceTable(currency, levelPrefix);
  const stops = [...(detail.stops ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const info = detail.internalInfo;
  const partner = info?.rebilling?.invoicePartnerId ? lookups.partners.find(p => p.id === info.rebilling.invoicePartnerId) : undefined;

  return {
    name: detail.name,
    transportationType: detail.header?.transportationType,
    expeditionType: detail.header?.expeditionType,
    truckTypeCode: detail.header?.truckTypeCode,
    loadingTypes: detail.header?.loadingTypes ?? [],
    cargoType: detail.header?.cargoType as LineForm['cargoType'],
    specialRequirements: detail.header?.requirements ?? [],
    carrierNote: detail.header?.carrierNote,

    capacity: {
      ladenFactor: detail.capacity?.ladenFactor ?? null,
      cargoTemplateCode: detail.capacity?.cargoTemplateCode ?? null,
      cargoTemplateName: detail.capacity?.cargoTemplateName ?? null,
    },
    allowPartialDelivery: !!detail.allowPartialDelivery,

    locations: stops.map(
      (stop, index): LineStop =>
        ({
          // The stop id doubles as the React key, so an edited line keeps its stops' identity.
          id: stop.id,
          stopId: stop.id,
          localityMode: LocalityEntryModeEnum.FROM_LOCATIONS,
          location: null,
          manualAddress: null,
          loadingReference: stop.loadingReference ?? null,
          // not edited here, carried so a save keeps them (they prefill the shipment's stop)
          requirements: stop.requirements?.length ? [...stop.requirements] : undefined,
          stopLocationNote: stop.locationNote ?? undefined,
          // Order ids are kept as stored: unloadingOrderIds of later stops refer to them.
          orders: stop.orders ?? [],
          unloadingOrderIds: stop.unloadingOrderIds?.length ? stop.unloadingOrderIds : index === stops.length - 1 ? [] : null,
          timeslots: { dayWithInterval: [] },
          notifySelectedPerson: false,
          candidates: (stop.candidateLocations ?? []).map(l => ({ key: generateUUID(), locationId: l.locationId, name: l.locationName })),
          openingHours: normalizeOpeningHours(stop.openingHours),
        }) as LineStop,
    ),

    distribution: null,
    contractedCarriers: (detail.distribution?.contractedCarriers ?? []).map(c => fromContractedCarrierOutDTO(c, priceTable)),
    priceTable,
    bothDirections: !!detail.bothDirections,
    intervalHours: detail.distribution?.intervalHours ?? DEFAULT_LINE_INTERVAL_HOURS,

    attachments: detail.attachments ?? [],
    existingAttachmentIds: (detail.attachments ?? []).map(a => a.attachmentId),

    person: lov(lookups.persons, info?.responsiblePersonId),
    disponentPerson: lov(lookups.persons, info?.disponentPersonId),
    folders: pickAccessibleFolders(info?.folderIds, lookups.folders),
    hiddenFolderIds: (info?.folderIds ?? []).filter(id => !lookups.folders.some(f => f.id === id)),
    notifiedTargets: info?.notifiedTargets ?? [],
    notifyByRole: !!info?.notifyByRole,
    invoiceState: info?.invoiceState,
    pairingSymbol: info?.pairingSymbol,
    internalNote: info?.internalNote,
    rebilling: info?.rebilling
      ? {
          invoiceRebillingType: info.rebilling.invoiceRebillingType,
          invoiceDepartment: info.rebilling.invoiceDepartment,
          invoicePartner: info.rebilling.invoicePartnerId
            ? { companyId: info.rebilling.invoicePartnerId, companyName: partner?.name ?? '' }
            : undefined,
        }
      : undefined,
    labels: (info?.labelIds ?? []).map(id => lookups.labels.find(l => l.labelId === id)).filter(l => !!l),
    hiddenLabelIds: (info?.labelIds ?? []).filter(id => !lookups.labels.some(l => l.labelId === id)),
    internalInfoAccountCodeListId: info?.internalInfoAccountCodeListId,
    internalInfoCostCenterCodeListId: info?.internalInfoCostCenterCodeListId,
    internalInfoCostUnitCodeListId: info?.internalInfoCostUnitCodeListId,
    internalInfoProjectCodeListId: info?.internalInfoProjectCodeListId,
  };
};

/** One entry per day that is not closed, e.g. "PONDĚLÍ 08:00–12:00, 13:00–16:00" or "STŘEDA celý den". */
export const summarizeOpeningWeek = (
  week: OpeningHourDefinitionsInDTO | undefined,
  dayLabel: (day: DayEnum) => string,
  allDayLabel: string,
): string[] =>
  (week?.openingHoursDays ?? [])
    .filter(d => d.openingMode !== OpeningModeEnum.CLOSED)
    .map(d => {
      const intervals = (d.openingHours ?? []).filter(i => i.since && i.till).map(i => `${i.since}–${i.till}`);
      const allDay = d.openingMode === OpeningModeEnum.OPEN || (intervals.length === 1 && intervals[0] === '00:00–24:00');
      // a day just opened, times not typed yet, says nothing yet
      if (!allDay && intervals.length === 0) return null;
      return `${dayLabel(d.day)} ${allDay ? allDayLabel : intervals.join(', ')}`;
    })
    .filter((entry): entry is string => !!entry);
